<template>
  <div class="to-do-view__layers">
    <ToDoContent
      v-if="!expandToDo"
      :class="className"
      class="to-do-view"
    >
      <template #header>
        <BaseHeading
          level="2"
          color="monochrome-06"
        >
          ToDo Items
        </BaseHeading>
      </template>
      <template #content>
        <div class="to-do__filters">
          <div class="to-do__menu">
            <button
              v-for="item in toDoMenu"
              :key="item.id"
              type="button"
              class="to-do__menu__item"
              :class="{ 'is-active': selectedMenuItem === item.id }"
              @click="onSelectMenuItem(item.id)"
            >
              {{ item.label }}: {{ formatToDoCount(item.count) }}
            </button>
          </div>
          <BasicCheckbox
            :modelValue="showCompleted"
            class="to-do__show-completed"
            label="Show Completed"
            value="showCompleted"
            checkType="switch"
            :lineThrough="false"
            @update:modelValue="onToggleCompleted"
          />
        </div>
        <div class="to-do__summary">
          <button
            v-for="stat in summaryStats"
            :key="stat.id"
            type="button"
            class="to-do__summary-item"
            :class="{ 'is-active': filterToDo[stat.id] }"
            :aria-pressed="Boolean(filterToDo[stat.id])"
            @click="onSelectSummaryStat(stat.id)"
          >
            <div class="to-do__summary-copy">
              <icon
                :data="stat.icon"
                width="var(--icon-16)"
                height="var(--icon-16)"
                :color="stat.color"
              />
              <span>{{ stat.label }}</span>
            </div>
            <span class="to-do__summary-count">{{ formatToDoCount(stat.count) }}</span>
          </button>
        </div>
        <ToDoListSkeleton v-if="$wait.is('get.todos')" />
        <template v-else-if="toDos.length">
          <Observer
            id="to-do-view-list"
            :key="selectedMenuItem"
            v-scroll="handleScroll"
            class="to-do__item__list custom-scroll"
            :items="toDos"
            :last="isLast"
            :options="observerOptions"
            @intersect="scroll"
            @onMounted="scrollToSelectedElement"
          >
            <template #default="{ item, index }">
              <ToDoItem
                :id="item.id"
                :key="item.id"
                :item="item"
                :index="index"
                :sm="true"
                :isSent="selectedMenuItem === 'sent'"
                :disableCheckbox="item.owners.every(owner => owner.id !== currentUser.id)"
                @onCheckboxClick="onCheckboxClick"
                @onEdit="onEditToDo"
                @onOpen="onOpenToDo"
                @onRelatedUrlClick="$emit('onHideToDoWidget')"
              />
            </template>
            <template #after>
              <li
                v-if="$wait.is('get.todos.scroll')"
                class="to-do__list-loader"
              >
                <SpinnerDots :size="6" />
              </li>
            </template>
          </Observer>
          <div class="content__box-shadow" />
        </template>
        <div
          v-else
          class="to-do__empty"
        >
          <BaseSubHeading
            size="extra-large"
            weight="400"
            color="monochrome-05"
            darkColor="monochrome-04"
          >
            Clean slate — enjoy it while it lasts
          </BaseSubHeading>
          <img
            :src="require(`@/assets/images/picture_cup${isDark ? '_dark' : ''}.png`)"
            alt=""
            class="to-do__empty-image"
          />
        </div>
      </template>
      <template #footer>
        <button
          id="to-do-add-button"
          type="button"
          class="link is-large to-do__add-button"
          @click.prevent="$emit('addToDo')"
        >
          <icon
            data="@icon/plus.svg"
            :fill="true"
            width="var(--icon-24)"
            height="var(--icon-24)"
          />
          ToDo
        </button>
      </template>
    </ToDoContent>
    <ToDoDetails
      v-if="openedToDo"
      id="to-do-details"
      :item="openedToDo"
      @back="onCloseDetails"
      @edit="onEditToDo"
      @onCheckboxClick="onCheckboxClick"
    />
    <ToDoAdd
      v-if="expandToDo"
      id="to-do-edit"
      className="to-do__edit"
      @onCancel="onCloseToDo"
    />
  </div>
</template>

<script lang="ts">
import { mixins, Options } from "vue-class-component"
import { Prop, Watch } from "vue-property-decorator"
import { isBefore, isSameDay } from "date-fns"
import { gsap } from "gsap"
import { Action, Getter } from "s-vuex-class"

import BasicCheckbox from "@/components/Form/BasicCheckbox.vue"
import Observer from "@/components/Observer/index.vue"
import SpinnerDots from "@/components/SpinnerDots/index.vue"
import ToDoAdd from "@/components/ToDo/ToDoAdd.vue"
import ToDoContent from "@/components/ToDo/ToDoContent.vue"
import ToDoDetails from "@/components/ToDo/ToDoDetails.vue"
import ToDoItem from "@/components/ToDo/ToDoItem.vue"
import ToDoListSkeleton from "@/components/ToDo/ToDoListSkeleton.vue"

import Debounce from "@/decorators/Debounce"
import { handleScrollOnElement } from "@/mixins/handleScrollOnElement"
import { FilterToDo, ServerResponse, User } from "@/models"
import { ToDo, ToDoMetrics, ToDoStatus } from "@/models/ToDo"
import { Actions, Getters } from "@/store"
import { getTodayCalendarDate, toCalendarDate } from "@/utils/dateUtil"
import { forwardScaleAnimation, reverseScaleAnimation } from "@/utils/gsapAnimation"

import type {
  ResourceEvent,
  TodoItemChangedNotificationDto,
  TodoItemMetricsUpdatedNotificationDto
} from "@/models/common"

const components = {
  ToDoContent,
  Observer,
  ToDoItem,
  ToDoAdd,
  ToDoDetails,
  ToDoListSkeleton,
  BasicCheckbox,
  SpinnerDots
}

@Options({
  name: "ToDoView",
  components
})
export default class ToDoView extends mixins(handleScrollOnElement) {
  @Prop({ default: "" }) readonly className: string
  @Prop() readonly selectedToDoInList: ToDo

  @Getter(Getters.GET_TODOS) readonly toDos: ToDo[]
  @Getter(Getters.GET_TODOS_SERVER_RESPONSE) readonly toDosServerResponse: ServerResponse<ToDo>
  @Getter(Getters.GET_USER_CURRENT_PROFILE) readonly currentUser: User
  @Getter(Getters.GET_CURRENT_ACCOUNT_ID) readonly currentAccountId: string
  @Getter(Getters.THEME_IS_DARK) readonly isDark: boolean
  @Getter(Getters.SUBSCRIPTION_MESSAGE_NOTIFICATION) readonly subscriptionNotification: (
    type: ResourceEvent
  ) => TodoItemChangedNotificationDto | TodoItemMetricsUpdatedNotificationDto | null

  @Action(Actions.GET_TODOS) getToDos: (filterToDo: FilterToDo) => void
  @Action(Actions.GET_TODO_METRICS) getToDoMetrics: (
    filterToDo: FilterToDo
  ) => Promise<ToDoMetrics | false>
  @Action(Actions.GET_OPENED_TODO_COUNT) getOpenedToDoCount: () => void
  @Action(Actions.TODO_ITEM_RESOLVE) toDoResolve: (todoItemId: string) => Promise<ToDo>
  @Action(Actions.TODO_ITEM_UNRESOLVE) toDoUnresolve: (todoItemId: string) => Promise<ToDo>
  @Action(Actions.REMOVE_TODO_FROM_LIST) removeToDoFromList: (todoItemId: string) => void
  @Action(Actions.SET_TODO) setToDo: (todo: ToDo) => void
  @Action(Actions.SET_TODO_IN_LIST) setToDoInList: (todo: ToDo) => void
  @Action(Actions.RESET_TO_DO) resetToDo: () => void
  @Action(Actions.RESET_TO_DOS) resetToDos: () => void

  filterToDo = new FilterToDo()
  selectedMenuItem = "assignedToMe"
  showCompleted = false
  expandToDo = false
  openedToDo: ToDo | null = null
  toDoMetrics: ToDoMetrics = new ToDoMetrics()
  observerOptions = {
    root: null,
    scrollMargin: "0px 0px 160px 0px",
    threshold: 0.5
  }
  get toDoMenu() {
    return [
      {
        id: "assignedToMe",
        label: "My ToDos",
        count: this.toDoMetrics.myTodos
      },
      {
        id: "sent",
        label: "Sent to Others",
        count: this.toDoMetrics.sentToOthers
      }
    ]
  }

  /** Displays backend metrics for ToDos matching the current filter. */
  get summaryStats() {
    return [
      {
        id: "urgent",
        label: "Urgent",
        count: this.toDoMetrics.urgent,
        icon: require("@icon/flag.svg"),
        color: "var(--serviceAlert-02)"
      },
      {
        id: "today",
        label: "Today",
        count: this.toDoMetrics.today,
        icon: require("@icon/clock.svg"),
        color: this.isDark ? "var(--serviceWarning-00)" : "var(--serviceWarning-03)"
      },
      {
        id: "pastDue",
        label: "Past Due",
        count: this.toDoMetrics.pastDue,
        icon: require("@icon/alert-triangle.svg"),
        color: "var(--serviceAlert-02)"
      }
    ]
  }

  get isLast() {
    return this.toDosServerResponse?.last ?? true
  }

  formatToDoCount(count: number) {
    return count === 0 ? "--" : count
  }

  get todoItemChangedNotification() {
    return this.subscriptionNotification("TODO_ITEM_CHANGED") as TodoItemChangedNotificationDto
  }

  get todoItemMetricsUpdatedNotification() {
    return this.subscriptionNotification(
      "TODO_ITEM_METRICS_UPDATED"
    ) as TodoItemMetricsUpdatedNotificationDto
  }

  async created() {
    this.$wait.start("get.todos")
    this.getMenuItemBySelectedToDo()
    this.onResetFilter()
    await Promise.all([this.onGetBySelectedToDos(), this.loadMetrics()])
    await this.getOpenedToDoCount()
    this.openedToDo = this.selectedToDoInList ?? null
    this.$wait.end("get.todos")
  }

  getMenuItemBySelectedToDo() {
    if (!this.selectedToDoInList) return
    if (!this.selectedToDoInList.owners.find(u => u.id === this.currentUser.id)) {
      this.selectedMenuItem = "sent"
      return
    }
    this.showCompleted = Boolean(
      this.selectedToDoInList.ownersCompletionStatuses[this.currentUser.id]
    )
    this.selectedMenuItem = "assignedToMe"
  }

  async onGetBySelectedToDos() {
    await this.getToDos(this.filterToDo)
    if (!this.selectedToDoInList) return

    if (this.filterToDo.page > this.toDosServerResponse.totalPages) return
    if (!this.toDos.find(t => t.id === this.selectedToDoInList.id)) {
      this.filterToDo.page++
      await this.onGetBySelectedToDos()
    }
  }

  async scrollToSelectedElement() {
    if (!this.selectedToDoInList) return
    const observer = document.getElementById("to-do-view-list")
    if (!observer) return
    const element = document.getElementById(`${this.selectedToDoInList.id}`)
    if (!element) return
    observer.scrollTop = element.offsetTop
    gsap.to(element, {
      keyframes: [
        { delay: 0.15 },
        { x: 5, duration: 0.1 },
        { x: -5, duration: 0.1 },
        { x: 3, duration: 0.1 },
        { x: -3, duration: 0.1 },
        { x: 0, duration: 0.1 }
      ]
    })
  }

  onResetFilter() {
    const { urgent, today, pastDue } = this.filterToDo
    this.filterToDo = new FilterToDo({ urgent, today, pastDue })
    this.filterToDo.addSort("createdDate", false)
    this.filterToDo.excludedStatuses = this.showCompleted ? undefined : ["COMPLETED"]
    switch (this.selectedMenuItem) {
      case "assignedToMe":
        this.filterToDo.myTodos = true
        this.filterToDo.completedByMe = this.showCompleted ? undefined : false
        break
      case "sent":
        this.filterToDo.createdByMe = true
        break
    }
    this.resetToDos()
  }

  /** Keeps the list in its skeleton state while menu and completion filters reload. */
  async onUpdateFilterToDo() {
    this.$wait.start("get.todos")
    this.onResetFilter()
    try {
      await Promise.all([this.getToDos(this.filterToDo), this.loadMetrics()])
    } finally {
      this.$wait.end("get.todos")
    }
  }

  async loadMetrics() {
    const metrics = await this.getToDoMetrics(this.filterToDo)
    if (metrics) {
      this.toDoMetrics = metrics
    }
  }

  /** Applies SSE patches only to ToDos already present in the current list. */
  @Watch("todoItemChangedNotification")
  async onTodoItemChanged(notification: TodoItemChangedNotificationDto) {
    if (!notification || notification.accountId !== this.currentAccountId) return

    const element = document.getElementById(notification.todoItemId)
    if (notification.changeType === "DELETED") {
      if (element) {
        await new Promise<void>(resolve => {
          reverseScaleAnimation(element).eventCallback("onComplete", resolve).play()
        })
      }
      this.removeToDoFromList(notification.todoItemId)
      if (this.openedToDo?.id === notification.todoItemId) this.openedToDo = null
      return
    }

    let todo: ToDo
    if (notification.changeType === "CREATED") {
      if (!notification.todoItem) return
      todo = new ToDo(notification.todoItem)
    } else {
      const currentTodo = this.toDos.find(todo => todo.id === notification.todoItemId)
      if (!currentTodo) return
      todo = new ToDo({ ...currentTodo, ...notification.changedFields })
    }

    const matchesCurrentFilter = this.matchesCurrentFilter(todo)
    if (matchesCurrentFilter) {
      this.setToDoInList(todo)
    } else {
      if (todo.status === "COMPLETED" || todo.completedByMe) {
        this.setToDoInList(todo)
        await this.$nextTick()
        await this.animateToDoRemoval(todo.id)
      }
      this.removeToDoFromList(todo.id)
    }
    if (this.openedToDo?.id === todo.id) this.openedToDo = todo

    if (notification.changeType === "CREATED" && matchesCurrentFilter) {
      await this.$nextTick()
      const createdElement = document.getElementById(todo.id)
      if (createdElement) {
        forwardScaleAnimation(createdElement).play()
      }
    }
  }

  /** Checks the menu, completion, and summary filters used by the visible ToDo list. */
  matchesCurrentFilter(todo: ToDo) {
    if (this.filterToDo.status && todo.status !== this.filterToDo.status) return false
    if (this.filterToDo.excludedStatuses?.includes(todo.status)) return false
    if (
      this.filterToDo.completedByMe !== undefined &&
      todo.completedByMe !== this.filterToDo.completedByMe
    )
      return false
    if (this.filterToDo.myTodos && !todo.owners.some(owner => owner.id === this.currentUser.id)) {
      return false
    }
    if (this.filterToDo.createdByMe && todo.createdBy.id !== this.currentUser.id) return false
    if (this.filterToDo.urgent && !todo.urgent) return false
    if (!todo.estimatedDeadline) return !this.filterToDo.today && !this.filterToDo.pastDue

    const deadline = toCalendarDate(todo.estimatedDeadline)
    const today = getTodayCalendarDate()
    if (this.filterToDo.today && !isSameDay(deadline, today)) return false
    if (this.filterToDo.pastDue && !isBefore(deadline, today)) return false
    return true
  }

  /** Refreshes the current filter's metrics when the backend marks them as stale. */
  @Watch("todoItemMetricsUpdatedNotification")
  @Debounce(200)
  onTodoItemMetricsUpdated(notification: TodoItemMetricsUpdatedNotificationDto) {
    if (!notification || notification.accountId !== this.currentAccountId) return
    this.loadMetrics()
  }

  onSelectMenuItem(id) {
    this.selectedMenuItem = id
    this.onUpdateFilterToDo()
  }

  onToggleCompleted(value: boolean) {
    this.showCompleted = value
    this.onUpdateFilterToDo()
  }

  /** Toggles one ToDo metrics filter at a time and shows the list skeleton while it reloads. */
  async onSelectSummaryStat(id: string) {
    this.$wait.start("get.todos")
    this.filterToDo[id] = this.filterToDo[id] ? undefined : true
    for (const filter of ["urgent", "today", "pastDue"]) {
      if (filter !== id) this.filterToDo[filter] = undefined
    }
    this.filterToDo.page = 1
    this.resetToDos()
    try {
      await Promise.all([this.getToDos(this.filterToDo), this.loadMetrics()])
    } finally {
      this.$wait.end("get.todos")
    }
  }

  async onCheckboxClick({ status, toDoItemId }: { status: ToDoStatus; toDoItemId: string }) {
    const waitKey = `todo.status.${toDoItemId}`
    if (this.$wait.is(waitKey)) return

    this.$wait.start(waitKey)
    try {
      switch (status) {
        case "ACTIVE":
          await this.toDoUnresolve(toDoItemId)
          break
        case "COMPLETED":
          await this.toDoResolve(toDoItemId)
      }
    } finally {
      this.$wait.end(waitKey)
    }
  }

  /** Lets the checkbox strikethrough finish, then plays the same scale-out used for deleted items. */
  async animateToDoRemoval(toDoItemId: string) {
    const element = document.getElementById(toDoItemId)
    if (!element) return

    await new Promise<void>(resolve => setTimeout(resolve, 450))
    await new Promise<void>(resolve => {
      reverseScaleAnimation(element).eventCallback("onComplete", resolve).play()
    })
  }

  /** Shows a compact loader while another page is appended to the current list. */
  async scroll() {
    if (this.$wait.is("get.todos.scroll") || this.isLast) return

    this.$wait.start("get.todos.scroll")
    this.filterToDo.page++
    try {
      await this.getToDos(this.filterToDo)
    } finally {
      this.$wait.end("get.todos.scroll")
    }
  }

  async onEditToDo(toDo: ToDo) {
    this.setToDo(toDo)
    this.expandToDo = true
    await this.$nextTick()
    this.animateScale("to-do-edit", "to-do-edit-button", 0, 1)
  }

  async onOpenToDo(toDo: ToDo) {
    this.openedToDo = toDo
    await this.$nextTick()
    this.animateScale("to-do-details", toDo.id, 0, 1)
  }

  /** Reverses the details scale only for Back, then removes the overlay after the list is revealed. */
  onCloseDetails() {
    const triggerId = this.openedToDo?.id
    if (!triggerId) {
      this.openedToDo = null
      return
    }

    this.animateScale("to-do-details", triggerId, 1, 0, () => {
      this.openedToDo = null
    })
  }

  onCloseToDo(dismissed: boolean | MouseEvent = false) {
    if (dismissed === true) {
      this.resetToDo()
      this.expandToDo = false
      this.openedToDo = null
      return
    }
    this.animateScale("to-do-edit", "to-do-edit-button", 1, 0, () => {
      this.resetToDo()
      this.expandToDo = false
    })
  }

  /** Scales an overlay from its trigger and completes safely if list updates removed either element. */
  animateScale(
    elementId: string,
    triggerId: string,
    from: number,
    to: number,
    onComplete?: () => void
  ) {
    const element = document.getElementById(elementId)
    const trigger = document.getElementById(triggerId)
    if (!element || !trigger) {
      onComplete?.()
      return
    }

    const elementRect = element.getBoundingClientRect()
    const triggerRect = trigger.getBoundingClientRect()

    gsap.fromTo(
      element,
      {
        scale: from,
        transformOrigin: `${triggerRect.left + triggerRect.width / 2 - elementRect.left}px ${
          triggerRect.top + triggerRect.height / 2 - elementRect.top
        }px`
      },
      {
        scale: to,
        duration: 0.3,
        ease: to ? "power3.out" : "power3.in",
        onComplete
      }
    )
  }
}
</script>
